<template>
  <div class="qe">
    <div class="qe-label" v-if="label">{{ label }}</div>
    <!-- Wrapper so we can position the variable select over the toolbar -->
    <div class="qe-wrap">
      <div ref="toolbar">
        <span class="ql-formats">
          <button class="ql-bold"></button>
          <button class="ql-italic"></button>
          <button class="ql-underline"></button>
        </span>
        <span class="ql-formats">
          <button class="ql-link"></button>
        </span>
        <span class="ql-formats">
          <button class="ql-list" value="ordered"></button>
          <button class="ql-list" value="bullet"></button>
        </span>
        <span class="ql-formats">
          <select class="ql-align">
            <option selected></option>
            <option value="center"></option>
            <option value="right"></option>
          </select>
        </span>
        <span class="ql-formats">
          <button class="ql-clean"></button>
        </span>
      </div>
      <!-- Variable select lives OUTSIDE ref="toolbar" so Quill never touches it -->
      <select
        v-if="showVariables"
        v-model="selectedVariable"
        @change="insertVariable"
        class="qe-var-select"
      >
        <option value="">{{ '{' + '{x}' + '}' }} Variables</option>
        <option v-for="v in variables" :key="v.value" :value="v.value">{{ v.label }}</option>
      </select>
    </div>
    <div ref="editor" class="qe-editor"></div>
  </div>
</template>

<script>
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { TEMPLATE_VARIABLES } from '../../helpers/email-variables';

export default {
  name: 'QuillEditor',

  props: {
    value: { type: String, default: '' },
    label: { type: String, default: '' },
    showVariables: { type: Boolean, default: true },
    placeholder: { type: String, default: 'Write your content here...' },
  },

  data() {
    return {
      quill: null,
      internalUpdate: false,
      selectedVariable: '',
      variables: TEMPLATE_VARIABLES,
      lastSelection: null,
    };
  },

  mounted() {
    var self = this;

    this.quill = new Quill(this.$refs.editor, {
      theme: 'snow',
      modules: {
        toolbar: this.$refs.toolbar,
      },
      placeholder: this.placeholder,
    });

    // Set initial content
    if (this.value) {
      this.quill.root.innerHTML = this.value;
    }

    // Track selection so we know where to insert variables
    this.quill.on('selection-change', function (range) {
      if (range) {
        self.lastSelection = range;
      }
    });

    // Emit on text change
    this.quill.on('text-change', function () {
      if (self.internalUpdate) return;
      var html = self.quill.root.innerHTML;
      if (html === '<p><br></p>') html = '';
      self.$emit('input', html);
    });
  },

  methods: {
    insertVariable: function () {
      var value = this.selectedVariable;
      if (!value) return;
      // Use last known selection (clicking the select blurs the editor)
      var range = this.lastSelection || { index: this.quill.getLength() - 1 };
      this.quill.focus();
      this.quill.insertText(range.index, value + ' ');
      this.quill.setSelection(range.index + value.length + 1);
      this.selectedVariable = '';
    },
  },

  watch: {
    value: function (newVal) {
      if (!this.quill) return;
      var current = this.quill.root.innerHTML;
      if (current === '<p><br></p>') current = '';
      if (newVal !== current) {
        this.internalUpdate = true;
        this.quill.root.innerHTML = newVal || '';
        this.internalUpdate = false;
      }
    },
  },

  beforeDestroy() {
    this.quill = null;
  },
};
</script>

<style scoped>
.qe {
  margin-bottom: 16px;
}
.qe-label {
  font-size: 13px;
  font-weight: 600;
  color: #5f6368;
  margin-bottom: 6px;
}
</style>

<style>
/* Quill overrides for the email editor context */
.qe .ql-toolbar.ql-snow {
  border: 1px solid #dadce0;
  border-radius: 6px 6px 0 0;
  background: #f8f9fa;
}
.qe .ql-container.ql-snow {
  border: 1px solid #dadce0;
  border-top: none;
  border-radius: 0 0 6px 6px;
  min-height: 120px;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 14px;
}
.qe .ql-editor {
  min-height: 120px;
}

/* Wrapper: positions the variable select over the Quill toolbar */
.qe .qe-wrap {
  position: relative;
}

/* Variable select — absolutely positioned in the toolbar's top-right */
.qe .qe-var-select {
  position: absolute;
  top: 6px;
  right: 8px;
  z-index: 1;
  height: 28px;
  padding: 0 24px 0 8px;
  border: 1px solid #c4c8d0;
  border-radius: 4px;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%235f6368'/%3E%3C/svg%3E") no-repeat right 7px center;
  background-size: 8px 5px;
  font-size: 12px;
  font-weight: 500;
  color: #5f6368;
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  outline: none;
  transition: border-color 0.15s;
}
.qe .qe-var-select:hover {
  border-color: var(--primary-color, #3b86f5);
  color: var(--primary-color, #3b86f5);
}
.qe .qe-var-select:focus {
  border-color: var(--primary-color, #3b86f5);
}
</style>
